import { summarizeStock, type StockOverview } from "./stockHealth";
import type { StockMatrixRow } from "../components/StockMatrixGrid";

export interface AllMarketsRow extends StockMatrixRow {
  // products.cogs as stored, before any conversion to the market's currency.
  // Absent without the gross-margin permission.
  stored_cogs?: number | null;
}

// One market's rows on screen and the warehouse columns still shown.
export interface ShownMarket {
  rows: AllMarketsRow[];
  warehouseIds: string[];
}

// Every market's totals added together. A product shown in two markets counts
// in each. Value and days of cover are left out: value is worked out from the
// stored cost (storedValue), and days of cover do not add across markets.
export function addMarkets(summaries: StockOverview[]): StockOverview {
  const out: StockOverview = {
    stock: 0, value: null, incoming: 0, doc: null,
    critical: 0, orderSoon: 0, overstock: 0, healthy: 0,
  };
  for (const s of summaries) {
    out.stock += s.stock;
    out.incoming += s.incoming;
    out.critical += s.critical;
    out.orderSoon += s.orderSoon;
    out.overstock += s.overstock;
    out.healthy += s.healthy;
  }
  return out;
}

// Inventory value of every shown row at its stored cost, in the stored cost's
// currency; null when any shown product has no cost.
export function storedValue(markets: ShownMarket[]): number | null {
  let total = 0;
  for (const m of markets) {
    const value = summarizeStock(
      m.rows.map((row) => ({ ...row, cogs: row.stored_cogs })),
      m.warehouseIds,
    ).value;
    if (value === null) return null;
    total += value;
  }
  return total;
}

// Display-only conversion through the USD-based rates; null when either rate
// is missing.
export function convertForDisplay(
  amount: number | null,
  from: string,
  to: string,
  rates: Record<string, number>,
): number | null {
  if (amount === null) return null;
  if (from === to) return amount;
  if (!(from in rates) || !(to in rates)) return null;
  return (amount / rates[from]) * rates[to];
}
